import {
  OkResponse,
  PackagingKit,
  PackagingKitList,
  SUPPLY_CATEGORY_LABELS,
  SUPPLY_UNIT_LABELS,
  Supply,
  SupplyCategory,
  SupplyList,
  type SupplyMovement,
  SupplyMovementList,
  SupplyUnit,
  formatBRL,
  formatDate,
} from '@deizi/shared';
import type { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field, Seg } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Drawer, Modal } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { Alert, EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, decimalInput, parseCentavos, parseDecimal } from '../../lib/numbers';
import { RestockDialog } from './RestockDialog';
import { AdjustSupplyPanel } from './StockPhonePage';
import { ExportCsvButton } from '../../components/ExportCsv';

const suppliesKey = ['supplies'] as const;
const kitsKey = ['packaging-kits'] as const;

function qtyText(q: number, unit: SupplyUnit): string {
  const u = SUPPLY_UNIT_LABELS[unit];
  return `${decimalInput(q, 3, 0)} ${q === 1 ? u.one : u.many}`;
}

// ---- supply form (new modal and edit drawer) ---------------------------------------------------

interface SupplyForm {
  name: string;
  category: SupplyCategory;
  unit: SupplyUnit;
  cost: string;
  minimum: string;
  stock: string;
}

const toSupplyForm = (s?: Supply): SupplyForm => ({
  name: s?.name ?? '',
  category: s?.category ?? 'hardware',
  unit: s?.unit ?? 'unit',
  cost: centavosInput(s?.unitCostCentavos ?? null),
  minimum: s?.minimumQuantity != null ? decimalInput(s.minimumQuantity, 3, 0) : '',
  stock: '0',
});

function supplyBody(f: SupplyForm) {
  return {
    name: f.name.trim(),
    category: f.category,
    unit: f.unit,
    unitCostCentavos: parseCentavos(f.cost) ?? 0,
    minimumQuantity: f.minimum.trim() === '' ? null : parseDecimal(f.minimum),
  };
}

function SupplyFields({
  form,
  setForm,
  touched,
  isNew = false,
}: {
  form: SupplyForm;
  setForm: (f: SupplyForm) => void;
  touched: boolean;
  isNew?: boolean;
}) {
  const nameBad = touched && form.name.trim() === '';
  const costBad = touched && parseCentavos(form.cost) === null;
  return (
    <>
      <Field id="s-name" label="Nome" error={nameBad ? 'Dê um nome ao insumo.' : null}>
        <input
          id="s-name"
          autoComplete="off"
          className={nameBad ? 'input err' : 'input'}
          placeholder="Ex.: Saquinho zip 10 × 15"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>
      <Field label="Tipo">
        <Seg
          label="Tipo"
          value={form.category}
          onChange={(v) => setForm({ ...form, category: v })}
          options={SupplyCategory.options.map((c) => ({
            value: c,
            label: SUPPLY_CATEGORY_LABELS[c],
          }))}
        />
      </Field>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))',
          gap: 12,
        }}
      >
        <Field id="s-unit" label="Unidade">
          <select
            id="s-unit"
            className="input"
            value={form.unit}
            onChange={(e) =>
              setForm({
                ...form,
                unit: SupplyUnit.options.find((u) => u === e.target.value) ?? 'unit',
              })
            }
          >
            {SupplyUnit.options.map((u) => (
              <option key={u} value={u}>
                {SUPPLY_UNIT_LABELS[u].one}
              </option>
            ))}
          </select>
        </Field>
        <Field id="s-cost" label="Custo unitário" error={costBad ? 'Informe o custo.' : null}>
          <AffixInput
            id="s-cost"
            prefix="R$"
            unit={`/${SUPPLY_UNIT_LABELS[form.unit].short}`}
            value={form.cost}
            onChange={(v) => setForm({ ...form, cost: v })}
            invalid={costBad}
          />
        </Field>
        {isNew ? (
          <Field id="s-stock" label="Estoque atual">
            <AffixInput
              id="s-stock"
              unit={SUPPLY_UNIT_LABELS[form.unit].short}
              value={form.stock}
              onChange={(v) => setForm({ ...form, stock: v })}
            />
          </Field>
        ) : null}
        <Field id="s-min" label="Mínimo" help="Abaixo disso entra na lista de compras.">
          <AffixInput
            id="s-min"
            value={form.minimum}
            onChange={(v) => setForm({ ...form, minimum: v })}
          />
        </Field>
      </div>
    </>
  );
}

const supplyValid = (f: SupplyForm) => f.name.trim() !== '' && parseCentavos(f.cost) !== null;

function NewSupplyDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(toSupplyForm());
  const [touched, setTouched] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/supplies',
        Supply,
        { ...supplyBody(form), quantityOnHand: parseDecimal(form.stock) ?? 0 },
        { idempotencyKey: idem },
      ),
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: suppliesKey });
      toast(`${s.name} cadastrado`);
      onClose();
    },
  });
  return (
    <Modal
      title="Novo insumo"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={create.isPending}
            onClick={() => {
              setTouched(true);
              if (supplyValid(form)) create.mutate();
            }}
          >
            Salvar insumo
          </button>
        </>
      }
    >
      <SupplyFields form={form} setForm={setForm} touched={touched} isNew />
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
    </Modal>
  );
}

const MOVE_LABELS: Record<z.infer<typeof SupplyMovement>['kind'], string> = {
  initial: 'Estoque inicial',
  purchase: 'Reposição',
  job: 'Usado em impressão',
  packing: 'Usado na embalagem',
  count: 'Contagem',
  loss: 'Perda ou avaria',
  use: 'Uso fora do sistema',
  other: 'Outro',
  return: 'Devolução',
};

function SupplyDrawer({ supply, onClose }: { supply: Supply; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adjusting, setAdjusting] = useState(false);
  const moves = useQuery({
    queryKey: [...suppliesKey, supply.id, 'movements', supply.quantityOnHand],
    queryFn: () => api.get(`/supplies/${supply.id}/movements`, SupplyMovementList),
  });
  const [form, setForm] = useState(toSupplyForm(supply));
  const [touched, setTouched] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: suppliesKey });
    void qc.invalidateQueries({ queryKey: kitsKey });
  };
  const save = useMutation({
    mutationFn: () => api.patch(`/supplies/${supply.id}`, Supply, supplyBody(form)),
    onSuccess: () => {
      refresh();
      toast('Insumo salvo');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/supplies/${supply.id}`, OkResponse),
    onSuccess: () => {
      refresh();
      toast('Insumo excluído. Fica na lixeira por 30 dias.');
      onClose();
    },
  });
  return (
    <Drawer kicker={SUPPLY_CATEGORY_LABELS[supply.category]} title={supply.name} onClose={onClose}>
      <div className="row wrap" style={{ gap: 8 }}>
        <span className={supply.low ? 'badge warn' : 'badge ok'}>
          {qtyText(supply.quantityOnHand, supply.unit)} em estoque
        </span>
        <span className="badge">
          usado em{' '}
          {supply.usedInKits.length > 0
            ? supply.usedInKits.map((k) => k.name).join(', ')
            : 'nenhum kit'}
        </span>
      </div>
      <SupplyFields form={form} setForm={setForm} touched={touched} />
      <p className="xs muted">Mudar o custo atualiza o valor dos kits que usam este item.</p>
      <div className="card sunk col" style={{ gap: 6, padding: 12, borderRadius: 12 }}>
        <div className="row between">
          <span className="lbl">Estoque</span>
          <button type="button" className="btn sm" onClick={() => setAdjusting(true)}>
            Ajustar estoque
          </button>
        </div>
        {(moves.data?.data ?? []).slice(0, 8).map((m) => (
          <div key={m.id} className="kv sm">
            <span>
              {MOVE_LABELS[m.kind]}
              {m.note ? ` · ${m.note}` : ''}
              <span className="xs muted" style={{ display: 'block' }}>
                {formatDate(m.at)} · {m.actor}
              </span>
            </span>
            <span className="v" style={{ color: m.quantity < 0 ? 'var(--err)' : 'var(--ok)' }}>
              {m.quantity > 0 ? '+' : '−'}
              {decimalInput(Math.abs(m.quantity), 3, 0)}
            </span>
          </div>
        ))}
        {moves.data && moves.data.data.length === 0 ? (
          <p className="sm muted">Sem movimentos ainda.</p>
        ) : null}
      </div>
      {adjusting ? <AdjustSupplyPanel supply={supply} onClose={() => setAdjusting(false)} /> : null}
      {save.isError || remove.isError ? (
        <Alert tone="err">{errorMessage(save.error ?? remove.error)}</Alert>
      ) : null}
      <div className="row wrap" style={{ gap: 10, justifyContent: 'space-between' }}>
        <button
          type="button"
          className="btn g"
          style={{ color: 'var(--err)' }}
          onClick={() => remove.mutate()}
        >
          Excluir
        </button>
        <button
          type="button"
          className="btn p"
          disabled={save.isPending}
          onClick={() => {
            setTouched(true);
            if (supplyValid(form)) save.mutate();
          }}
        >
          Salvar
        </button>
      </div>
    </Drawer>
  );
}

// ---- kits ---------------------------------------------------------------------------------------

function KitDialog({
  kit,
  supplies,
  onClose,
}: {
  kit: PackagingKit | null;
  supplies: Supply[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(kit?.name ?? '');
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries((kit?.items ?? []).map((i) => [i.supplyId, decimalInput(i.quantity, 3, 0)])),
  );
  const [extras, setExtras] = useState(
    (kit?.extraLines ?? []).map((l) => ({ name: l.name, cost: centavosInput(l.costCentavos) })),
  );
  const [touched, setTouched] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const ordered = [...supplies].toSorted(
    (a, b) => Number(b.category === 'packaging') - Number(a.category === 'packaging'),
  );
  const items = Object.entries(qty)
    .map(([supplyId, q]) => ({ supplyId, quantity: parseDecimal(q) ?? 0 }))
    .filter((i) => i.quantity > 0 && supplies.some((s) => s.id === i.supplyId));
  const extraLines = extras
    .filter((e) => e.name.trim() !== '')
    .map((e) => ({ name: e.name.trim(), costCentavos: parseCentavos(e.cost) ?? 0 }));
  const total =
    items.reduce(
      (sum, i) =>
        sum + (supplies.find((s) => s.id === i.supplyId)?.unitCostCentavos ?? 0) * i.quantity,
      0,
    ) + extraLines.reduce((sum, e) => sum + e.costCentavos, 0);
  const save = useMutation({
    mutationFn: () =>
      kit
        ? api.patch(`/packaging-kits/${kit.id}`, PackagingKit, {
            name: name.trim(),
            items,
            extraLines,
          })
        : api.post(
            '/packaging-kits',
            PackagingKit,
            { name: name.trim(), items, extraLines },
            { idempotencyKey: idem },
          ),
    onSuccess: (k) => {
      void qc.invalidateQueries({ queryKey: kitsKey });
      void qc.invalidateQueries({ queryKey: suppliesKey });
      toast(`${k.name}: ${formatBRL(k.totalCentavos)} por envio`);
      onClose();
    },
  });
  const nameBad = touched && name.trim() === '';
  return (
    <Modal
      title={kit ? 'Editar kit' : 'Novo kit'}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="grow sm">
            Custo do kit: <b className="tab">{formatBRL(Math.round(total))}</b>
          </span>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTouched(true);
              if (name.trim() !== '') save.mutate();
            }}
          >
            Salvar kit
          </button>
        </>
      }
    >
      <Field id="k-name" label="Nome do kit" error={nameBad ? 'Dê um nome ao kit.' : null}>
        <input
          id="k-name"
          autoComplete="off"
          className={nameBad ? 'input err' : 'input'}
          placeholder="Ex.: Caixa grande"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <span className="lbl">Itens e quantidade por envio</span>
      {supplies.length === 0 ? (
        <p className="sm muted">
          Cadastre insumos de embalagem primeiro (caixas, envelopes, fita).
        </p>
      ) : null}
      <div className="card flat" style={{ borderRadius: 12 }}>
        <div className="gt">
          {ordered.map((s) => {
            const on = (parseDecimal(qty[s.id] ?? '') ?? 0) > 0;
            return (
              <div
                key={s.id}
                className="gtr"
                style={{ gridTemplateColumns: '28px 1.6fr 140px 100px', padding: '6px 14px' }}
              >
                <button
                  type="button"
                  className={on ? 'box on' : 'box'}
                  aria-label={`Incluir ${s.name}`}
                  aria-pressed={on}
                  onClick={() => setQty({ ...qty, [s.id]: on ? '' : '1' })}
                >
                  {on ? <Icon name="check" size={14} strokeWidth={3} /> : null}
                </button>
                <span>
                  {s.name}
                  <span className="xs muted" style={{ display: 'block' }}>
                    {formatBRL(s.unitCostCentavos)}/{SUPPLY_UNIT_LABELS[s.unit].short}
                  </span>
                </span>
                <AffixInput
                  ariaLabel={`Quantidade de ${s.name}`}
                  unit={SUPPLY_UNIT_LABELS[s.unit].short}
                  value={qty[s.id] ?? ''}
                  onChange={(v) => setQty({ ...qty, [s.id]: v })}
                />
                <span className="num sm">
                  {on
                    ? formatBRL(
                        Math.round(s.unitCostCentavos * (parseDecimal(qty[s.id] ?? '') ?? 0)),
                      )
                    : '—'}
                </span>
              </div>
            );
          })}
          {extras.map((e, i) => (
            <div
              key={`x${i}`}
              className="gtr"
              style={{ gridTemplateColumns: '28px 1.6fr 140px 100px', padding: '6px 14px' }}
            >
              <span />
              <input
                className="cell"
                aria-label="Item fora do estoque"
                placeholder="Item fora do estoque"
                value={e.name}
                onChange={(ev) =>
                  setExtras(extras.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)))
                }
              />
              <AffixInput
                ariaLabel="Custo do item"
                prefix="R$"
                value={e.cost}
                onChange={(v) => setExtras(extras.map((x, j) => (j === i ? { ...x, cost: v } : x)))}
              />
              <button
                type="button"
                className="btn sm g"
                aria-label="Remover item"
                onClick={() => setExtras(extras.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="btn sm g"
        style={{ alignSelf: 'flex-start', padding: 0 }}
        onClick={() => setExtras([...extras, { name: '', cost: '' }])}
      >
        + Item fora do estoque
      </button>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

function KitsSection({ supplies }: { supplies: Supply[] }) {
  const kits = useQuery({
    queryKey: kitsKey,
    queryFn: () => api.get('/packaging-kits?limit=200', PackagingKitList),
  });
  const [editing, setEditing] = useState<PackagingKit | 'new' | null>(null);
  const list = kits.data?.data ?? [];
  return (
    <section className="col" style={{ gap: 14 }}>
      <div className="row between wrap">
        <h2>Kits de embalagem</h2>
        <button type="button" className="btn sm" onClick={() => setEditing('new')}>
          + Kit
        </button>
      </div>
      {kits.isPending ? <Skeleton rows={1} height={120} /> : null}
      {kits.isError ? (
        <ErrorBox title="Não foi possível carregar os kits" onRetry={() => void kits.refetch()} />
      ) : null}
      {kits.isSuccess && list.length === 0 ? (
        <p className="sm muted">
          Nenhum kit ainda. Monte “Envelope pequeno” ou “Caixa média” com os insumos de embalagem.
        </p>
      ) : null}
      <div className="grid">
        {list.map((k) => (
          <div key={k.id} className="card col" style={{ gap: 10 }}>
            <div className="row between">
              <h3>{k.name}</h3>
              <span className="fr tab" style={{ fontSize: 20, color: 'var(--pe)' }}>
                {formatBRL(k.totalCentavos)}
              </span>
            </div>
            <p className="xs muted">
              {k.defaultFor.length > 0
                ? `Padrão em ${k.defaultFor.map((c) => c.name).join(', ')}`
                : 'Não é padrão em nenhum canal'}
            </p>
            {k.shipmentsPossible !== null ? (
              <p
                className="xs"
                style={{ color: k.lowItems.length ? 'var(--warn)' : 'var(--ink2)' }}
              >
                Dá para {k.shipmentsPossible} {k.shipmentsPossible === 1 ? 'envio' : 'envios'}
                {k.lowItems.length ? `: ${k.lowItems.join(', ')} abaixo do mínimo` : ''}
              </p>
            ) : null}
            <div className="col" style={{ gap: 2 }}>
              {k.items.map((i) => (
                <div key={i.supplyId} className="kv">
                  <span className="sm">
                    {i.name} · {qtyText(i.quantity, i.unit)}
                  </span>
                  <span className="v sm">{formatBRL(i.subtotalCentavos)}</span>
                </div>
              ))}
              {k.extraLines.map((l) => (
                <div key={l.name} className="kv">
                  <span className="sm">
                    {l.name} <span className="xs muted">(fora do estoque)</span>
                  </span>
                  <span className="v sm">{formatBRL(l.costCentavos)}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn sm"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setEditing(k)}
            >
              Editar
            </button>
          </div>
        ))}
      </div>
      {editing ? (
        <KitDialog
          kit={editing === 'new' ? null : editing}
          supplies={supplies}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}

/** Estoque · Insumos (design Insumos). */
export function SuppliesPage() {
  const list = useQuery({
    queryKey: suppliesKey,
    queryFn: () => api.get('/supplies?limit=200', SupplyList),
  });
  const [category, setCategory] = useState<SupplyCategory | 'all'>('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [restocking, setRestocking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const all = list.data?.data ?? [];
  const shown = all.filter(
    (s) => (category === 'all' || s.category === category) && (!lowOnly || s.low),
  );
  const lowCount = all.filter((s) => s.low).length;
  const open = all.find((s) => s.id === openId) ?? null;
  const cols = '2fr 1fr 1fr 1.1fr 1fr 70px 1.3fr';

  return (
    <>
      <PageHead
        crumb="Estoque"
        title="Insumos"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <ExportCsvButton dataset="insumos" />
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              <Icon name="plus" size={18} strokeWidth={2.4} />
              Insumo
            </button>
            <button
              type="button"
              className="btn p"
              disabled={all.length === 0}
              onClick={() => setRestocking(true)}
            >
              Registrar reposição
            </button>
          </div>
        }
      />
      {list.isPending ? <Skeleton rows={4} height={48} /> : null}
      {list.isError ? (
        <ErrorBox
          title="Não foi possível carregar os insumos"
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {list.isSuccess && all.length === 0 ? (
        <section className="card">
          <EmptyState
            title="Nenhum insumo ainda"
            action={
              <button type="button" className="btn p" onClick={() => setCreating(true)}>
                + Insumo
              </button>
            }
          >
            Cadastre ímãs, argolas, caixas e envelopes para montar os kits de embalagem e o custo
            sair certo.
          </EmptyState>
        </section>
      ) : null}
      {all.length > 0 ? (
        <section className="card flat">
          <div className="row wrap" style={{ padding: '14px 16px', gap: 12 }}>
            <Seg
              label="Tipo"
              value={category}
              onChange={setCategory}
              options={[
                { value: 'all' as const, label: `Todos · ${all.length}` },
                ...SupplyCategory.options.map((c) => ({
                  value: c,
                  label: `${SUPPLY_CATEGORY_LABELS[c]} · ${all.filter((s) => s.category === c).length}`,
                })),
              ]}
            />
            <button
              type="button"
              className="check sm"
              role="switch"
              aria-checked={lowOnly}
              onClick={() => setLowOnly(!lowOnly)}
            >
              <span className={lowOnly ? 'toggle on' : 'toggle'} aria-hidden="true" />
              Só abaixo do mínimo · {lowCount}
            </button>
          </div>
          <div className="gt">
            <div className="gtr head stack-sm" style={{ gridTemplateColumns: cols }}>
              <span>Item</span>
              <span>Tipo</span>
              <span className="num">Custo un.</span>
              <span className="num">Em estoque</span>
              <span className="num">Mínimo</span>
              <span />
              <span>Kits</span>
            </div>
            {shown.map((s) => (
              <button
                key={s.id}
                type="button"
                className="gtr stack-sm"
                style={{ gridTemplateColumns: cols }}
                onClick={() => setOpenId(s.id)}
              >
                <span style={{ fontWeight: 500 }}>{s.name}</span>
                <span>{SUPPLY_CATEGORY_LABELS[s.category]}</span>
                <span className="num">
                  {formatBRL(s.unitCostCentavos)}/{SUPPLY_UNIT_LABELS[s.unit].short}
                </span>
                <span className="num" style={{ color: s.low ? 'var(--warn)' : undefined }}>
                  {qtyText(s.quantityOnHand, s.unit)}
                </span>
                <span className="num">
                  {s.minimumQuantity != null ? qtyText(s.minimumQuantity, s.unit) : '—'}
                </span>
                <span>{s.low ? <span className="badge warn">Baixo</span> : null}</span>
                <span className="sm muted trunc">
                  {s.usedInKits.map((k) => k.name).join(', ') || '—'}
                </span>
              </button>
            ))}
            {shown.length === 0 ? (
              <p className="sm muted" style={{ padding: 16 }}>
                Nenhum insumo com esses filtros.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}
      {list.isSuccess ? <KitsSection supplies={all} /> : null}
      {creating ? <NewSupplyDialog onClose={() => setCreating(false)} /> : null}
      {restocking ? <RestockDialog supplies={all} onClose={() => setRestocking(false)} /> : null}
      {open ? <SupplyDrawer key={open.id} supply={open} onClose={() => setOpenId(null)} /> : null}
    </>
  );
}
